import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, FileSpreadsheet, PanelLeftClose, PanelLeftOpen, Search, Star, X } from "lucide-react";
import { CATEGORY_ICONS, CATEGORY_COLORS } from "../reportsNavConfig";

const accordionMotion = {
  collapsed: { height: 0, opacity: 0 },
  expanded: { height: "auto", opacity: 1, transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] } },
};

export default function ReportsNavigationPanel({
  categoryOrder,
  reportsByCategory,
  selectedKey,
  selectedReport,
  reportSearch,
  onReportSearchChange,
  favorites,
  onToggleFavorite,
  onSelectReport,
  isItemActive,
  onCloseMobile,
  mobileOpen = false,
  collapsed = false,
  onToggleCollapsed,
  theme = "light",
}) {
  const [expanded, setExpanded] = useState(() => new Set());

  const activeCategory = useMemo(() => {
    if (!selectedReport?.category) return null;
    return selectedReport.category;
  }, [selectedReport]);

  const searchActive = reportSearch.trim().length > 0;

  useEffect(() => {
    if (searchActive) {
      const withResults = categoryOrder.filter((cat) => (reportsByCategory[cat] || []).length > 0);
      setExpanded(new Set(withResults));
      return;
    }
    if (activeCategory) {
      setExpanded((prev) => new Set([...prev, activeCategory]));
      return;
    }
    // Landing / guide: open the first category so users can see reports immediately.
    if (categoryOrder.length) {
      setExpanded((prev) => (prev.size ? prev : new Set([categoryOrder[0]])));
    }
  }, [activeCategory, searchActive, categoryOrder, reportsByCategory]);

  const toggleCategory = useCallback((cat) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  const handleSelect = (report) => {
    onReportSearchChange("");
    onSelectReport(report);
    onCloseMobile?.();
  };

  const renderReportItem = (item) => {
    const isActive = isItemActive(item);
    const isFav = favorites.includes(item.key);

    return (
      <button
        key={item.href || item.key}
        type="button"
        className={`prw-nav-item ${isActive ? "is-active" : ""}`}
        onClick={() => handleSelect(item)}
      >
        <span className="prw-nav-item__label">{item.name}</span>
        <span
          role="button"
          tabIndex={-1}
          className={`prw-nav-item__star ${isFav ? "is-fav" : ""}`}
          onClick={(e) => onToggleFavorite(item.key, e)}
          aria-label={isFav ? "Remove favourite" : "Pin favourite"}
        >
          <Star size={12} fill={isFav ? "currentColor" : "none"} />
        </span>
      </button>
    );
  };

  const panelContent = (
    <div className="prw-nav-panel__inner" data-theme={theme}>
      <div className="prw-nav-panel__head">
        <h2 className="prw-nav-panel__title">Reports</h2>
        {onToggleCollapsed && (
          <button
            type="button"
            className="prw-nav-panel__collapse-btn"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        )}
        {onCloseMobile && (
          <button
            type="button"
            className="prw-nav-panel__close-btn"
            onClick={onCloseMobile}
            aria-label="Close navigation"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className="prw-nav-panel__search-wrap">
        <Search size={15} className="prw-nav-panel__search-icon" aria-hidden />
        <input
          type="search"
          className="prw-nav-panel__search"
          placeholder="Search reports…"
          value={reportSearch}
          onChange={(e) => onReportSearchChange(e.target.value)}
          aria-label="Search reports"
        />
        {reportSearch && (
          <button
            type="button"
            className="prw-nav-panel__search-clear"
            onClick={() => onReportSearchChange("")}
            aria-label="Clear search"
          >
            <X size={13} />
          </button>
        )}
      </div>

      <nav className="prw-nav-panel__categories" aria-label="Report categories">
        {categoryOrder.map((cat) => {
          const CatIcon = CATEGORY_ICONS[cat] || FileSpreadsheet;
          const items = reportsByCategory[cat] || [];
          const hasContent = items.length > 0;
          const isOpen = expanded.has(cat);
          const hasActive = items.some((r) => isItemActive(r));

          if (!hasContent && cat !== "Custom Reports") return null;

          const catColor = CATEGORY_COLORS[cat];

          return (
            <div
              key={cat}
              className={`prw-nav-card ${isOpen ? "is-open" : ""} ${hasActive ? "has-active" : ""}`}
              style={catColor ? { borderColor: isOpen || hasActive ? catColor.border : undefined } : undefined}
            >
              <button
                type="button"
                className="prw-nav-card__trigger"
                onClick={() => hasContent && toggleCategory(cat)}
                aria-expanded={isOpen}
                disabled={!hasContent}
              >
                <span
                  className="prw-nav-card__icon"
                  style={catColor ? { background: catColor.bg, color: catColor.icon } : undefined}
                >
                  <CatIcon size={16} />
                </span>
                <span className="prw-nav-card__label">{cat}</span>
                {hasContent ? (
                  <>
                    <span className="prw-nav-card__count">{items.length}</span>
                    <ChevronDown size={14} className={`prw-nav-card__chevron ${isOpen ? "is-flipped" : ""}`} />
                  </>
                ) : (
                  cat === "Custom Reports" && <span className="prw-nav-card__soon">Soon</span>
                )}
              </button>

              <AnimatePresence initial={false}>
                {isOpen && hasContent && (
                  <motion.div
                    className="prw-nav-card__body"
                    initial="collapsed"
                    animate="expanded"
                    exit="collapsed"
                    variants={accordionMotion}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="prw-nav-card__items">
                      {items.map((item) => renderReportItem(item))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </nav>
    </div>
  );

  return (
    <>
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="prw-nav-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCloseMobile}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <aside
        className={`prw-nav-panel ${collapsed ? "is-collapsed" : ""} ${mobileOpen ? "is-mobile-open" : ""}`}
        aria-label="Reports navigation"
      >
        {panelContent}
      </aside>
    </>
  );
}
